import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Email as EmailIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Logo from '../components/Logo';

const LoginPage: React.FC = () => {
  const theme = useTheme();
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Design System: Nautilus Console
  const consoleColors = {
    deepOcean: '#0f2134',
    abyssal: '#0a1622',
    sand: '#ccba9b',
    sandDim: 'rgba(204, 186, 155, 0.5)',
    text: '#ffffff',
    textDim: 'rgba(255, 255, 255, 0.7)',
  };

  // Get the intended destination from navigation state or query params
  const queryParams = new URLSearchParams(location.search);
  const returnUrl = queryParams.get('returnUrl');
  const fromLocation = location.state?.from as { pathname?: string; search?: string; hash?: string } | undefined;
  const fromPath = fromLocation?.pathname
    ? `${fromLocation.pathname}${fromLocation.search || ''}${fromLocation.hash || ''}`
    : '/';
  const from = returnUrl || fromPath;


  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      if (from.startsWith('/oauth') || from.startsWith('http')) {
        window.location.href = from;
      } else {
        navigate(from, { replace: true });
      }
    }
  }, [isAuthenticated, navigate, from]);

  const handleInputChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    if (error) setError(null);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!formData.email.trim() || !formData.password.trim()) {
      setError('Please enter both email and password');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await login(formData.email.trim(), formData.password);

      if (result.success) {
        console.log('Login successful');
      } else {
        setError(result.error || 'Login failed');
      }
    } catch (err: any) {
      console.error('Login error:', err);
      setError(
        err.response?.data?.error ||
        err.response?.data?.message ||
        'Login failed. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSubmit(event);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: consoleColors.deepOcean, // Deep Immersion
        position: 'relative',
        p: 2,
        backgroundImage: `
            radial-gradient(circle at 50% 50%, ${alpha(consoleColors.sand, 0.05)} 0%, transparent 50%),
            linear-gradient(0deg, rgba(0,0,0,0.2) 0%, transparent 100%)
        `,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: 0, // Reset padding for strict layout
          width: '100%',
          maxWidth: 420,
          borderRadius: 2,
          position: 'relative',
          overflow: 'hidden',
          backgroundColor: consoleColors.abyssal,
          border: `1px solid ${consoleColors.sand}`, // Technical Border
          boxShadow: `0 0 20px ${alpha(consoleColors.deepOcean, 0.8)}`,
        }}
      >
        {/* Technical Header */}
        <Box sx={{
          p: 3,
          borderBottom: `1px solid ${alpha(consoleColors.sand, 0.2)}`,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          backgroundColor: alpha(consoleColors.sand, 0.03)
        }}>
          <Box sx={{ mb: 2 }}>
            <Logo size="large" clickable={false} showText={true} invertColors={true} />
          </Box>
          <Typography
            variant="overline"
            sx={{
              color: consoleColors.sand,
              letterSpacing: '0.2em',
              fontWeight: 700,
              fontSize: '0.75rem',
              opacity: 0.9
            }}
          >
            Secure Workspace Access
          </Typography>
        </Box>

        <Box sx={{ p: 4 }}>
          {/* Error Alert */}
          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 3,
                backgroundColor: 'rgba(60, 20, 20, 0.6)',
                color: '#ffaaaa',
                border: '1px solid #ff4444',
                '& .MuiAlert-icon': { color: '#ff4444' }
              }}
            >
              {error}
            </Alert>
          )}

          {/* Login Form */}
          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              fullWidth
              label="IDENTITY / EMAIL"
              type="email"
              value={formData.email}
              onChange={handleInputChange('email')}
              onKeyPress={handleKeyPress}
              disabled={loading}
              variant="filled"
              sx={{
                mb: 2,
                '& .MuiFilledInput-root': {
                  backgroundColor: 'rgba(0,0,0,0.2)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 1,
                  color: consoleColors.text,
                  '&:hover': {
                    backgroundColor: 'rgba(0,0,0,0.3)',
                    borderColor: consoleColors.sandDim,
                  },
                  '&.Mui-focused': {
                    backgroundColor: 'rgba(0,0,0,0.3)',
                    borderColor: consoleColors.sand,
                  }
                },
                '& .MuiInputLabel-root': {
                  color: consoleColors.textDim,
                  fontFamily: 'monospace',
                  fontSize: '0.8rem',
                  '&.Mui-focused': {
                    color: consoleColors.sand,
                  }
                }
              }}
              InputProps={{
                disableUnderline: true,
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon sx={{ color: consoleColors.sandDim }} />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="ACCESS KEY / PASSWORD"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleInputChange('password')}
              onKeyPress={handleKeyPress}
              disabled={loading}
              variant="filled"
              sx={{
                mb: 4,
                '& .MuiFilledInput-root': {
                  backgroundColor: 'rgba(0,0,0,0.2)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 1,
                  color: consoleColors.text,
                  '&:hover': {
                    backgroundColor: 'rgba(0,0,0,0.3)',
                    borderColor: consoleColors.sandDim,
                  },
                  '&.Mui-focused': {
                    backgroundColor: 'rgba(0,0,0,0.3)',
                    borderColor: consoleColors.sand,
                  }
                },
                '& .MuiInputLabel-root': {
                  color: consoleColors.textDim,
                  fontFamily: 'monospace',
                  fontSize: '0.8rem',
                  '&.Mui-focused': {
                    color: consoleColors.sand,
                  }
                }
              }}
              InputProps={{
                disableUnderline: true,
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon sx={{ color: consoleColors.sandDim }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      disabled={loading}
                      sx={{ color: consoleColors.textDim }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="outlined"
              size="large"
              disabled={loading || !formData.email.trim() || !formData.password.trim()}
              sx={{
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 600,
                letterSpacing: '0.1em',
                borderRadius: 1,
                textTransform: 'uppercase',
                color: consoleColors.sand,
                borderColor: consoleColors.sand,
                borderWidth: '1px',
                transition: 'all 0.2s',
                '&:hover': {
                  backgroundColor: alpha(consoleColors.sand, 0.1),
                  borderColor: consoleColors.sand,
                  borderWidth: '1px',
                  boxShadow: `0 0 15px ${alpha(consoleColors.sand, 0.3)}`
                },
                '&:disabled': {
                  borderColor: 'rgba(255,255,255,0.1)',
                  color: 'rgba(255,255,255,0.2)'
                }
              }}
            >
              {loading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} sx={{ color: consoleColors.sand }} />
                  AUTHENTICATING...
                </Box>
              ) : (
                'INITIATE SESSION'
              )}
            </Button>
          </Box>
        </Box>

        {/* Technical Footer */}
        <Box sx={{
          p: 2,
          textAlign: 'center',
          borderTop: `1px solid ${alpha(consoleColors.sand, 0.1)}`,
          backgroundColor: 'rgba(0,0,0,0.1)'
        }}>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)', fontFamily: 'monospace' }}>
            RESTRICTED ACCESS // AUTHORIZED PERSONNEL ONLY
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default LoginPage;